import { Info } from 'lucide-react'
import React from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { type EmployerICHRAConfig } from 'types'
import { getLocaleCurrencyStringFromString, months } from 'utils'
import { type BrokersEnrollmentWithPaymentsOrCoverages } from 'utils/web_api.schemas'

import BrokersEnrollmentStatusOutput from './BrokersEnrollmentStatusOutput'
import Coverages from './Coverages'

type BrokersMembersEnrollmentStatusOutputProps = {
  memberId: string
  enrollment: BrokersEnrollmentWithPaymentsOrCoverages
  isEditEnrollmentV2Enabled: boolean
  employerIchraConfig: EmployerICHRAConfig
  isTieredAllowancesEmployer: boolean
  isUserThatchAdmin: boolean
  planYear: number
}

const BrokersMembersEnrollmentStatusOutput = ({
  memberId,
  enrollment,
  employerIchraConfig,
  isTieredAllowancesEmployer,
  isEditEnrollmentV2Enabled,
  isUserThatchAdmin,
  planYear,
}: BrokersMembersEnrollmentStatusOutputProps) => {
  const enrollmentId = enrollment.id

  const lossAmount = enrollment.loss_amount
  return (
    <BrokersMemberContext.Provider
      value={{
        memberId,
        planYear,
        employerIchraConfig,
        isTieredAllowancesEmployer,
        isUserThatchAdmin,
      }}
    >
      <div className='py-2'>
        <BrokersEnrollmentStatusOutput
          memberId={memberId}
          enrollmentId={enrollmentId}
          handleActionError={() => {}}
          addLinkToTransactionsPage={false}
          hasEnrollmentPayments
        />
      </div>
      <div className='flex items-start justify-between py-2'>
        <PaymentStatusOutput enrollment={enrollment} />

        {isEditEnrollmentV2Enabled && (
          <Coverages
            enrollmentId={enrollmentId}
            memberId={memberId}
            editCoveragesPath=''
            isEditEnrollmentV2Enabled={isEditEnrollmentV2Enabled}
          />
        )}
      </div>
      {!!parseFloat(lossAmount.value ?? '0') && (
        <div className='mt-2 mb-2 text-red-800 space-x-2 flex items-center'>
          <span className='font-semibold'>Thatch Loss Amount:</span>
          <span>{getLocaleCurrencyStringFromString(lossAmount.value ?? '0')}</span>
          <DisplayTooltipOnHover text="This represents the amount Thatch will be covering for this enrollment. This amount will not be included in the employer's invoice.">
            <Info size={16} />
          </DisplayTooltipOnHover>
        </div>
      )}
    </BrokersMemberContext.Provider>
  )
}

const PaymentStatusOutput = ({
  enrollment,
}: {
  enrollment: BrokersEnrollmentWithPaymentsOrCoverages
}) => {
  const {
    payment_standing: paymentStanding,
    coverage_start_date_month: startMonth,
  } = enrollment
  const {
    possible_payment_amounts: possiblePaymentAmounts,
    paid_amount: paidAmount,
  } = paymentStanding

  return (
    <div className='flex items-center justify-between mb-4 min-w-[400px]'>
      <div className='grid grid-cols-5 gap-2'>
        <div className='col-span-2 font-bold'>Time Range</div>
        <div className='col-span-1 font-bold text-center'>Expected</div>
        <div className='col-span-1 font-bold text-center'>Observed</div>
        <div className='col-span-1 font-bold text-center'>Delta</div>
        {possiblePaymentAmounts?.map(({ amount, month }) => {
          return (
            <React.Fragment key={month}>
              <div className='col-span-2'>
                {startMonth && months[startMonth - 1]} - {month && months[month - 1]}
                {startMonth && month && ` (${month - startMonth + 1} mo)`}
              </div>
              <div className='col-span-1 text-center'>
                {getLocaleCurrencyStringFromString(amount)}
              </div>
              <div className='col-span-1 text-center'>
                {getLocaleCurrencyStringFromString(paidAmount.value ?? '0')}
              </div>
              <div className='col-span-1 text-center'>
                {getLocaleCurrencyStringFromString(
                  `${
                    (parseFloat(paidAmount.value ?? '0') * 100 -
                      parseFloat(amount) * 100) /
                    100
                  }`
                )}
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default BrokersMembersEnrollmentStatusOutput
